import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from './auth';
import { setBearerAuthorization } from './bearerAuthorization';
import { notifySessionExpired } from './sessionExpiry';

// Derive default Ontology Service base URL from current origin so the app works behind a
// reverse proxy. nginx exposes ontology-service under /api/ontology and strips the prefix.
const defaultOntologyBase =
  process.env.REACT_APP_ONTOLOGY_API_URL ||
  `${window.location.origin.replace(/\/$/, '')}/api/ontology`;

// Separate axios instance for the Ontology Service (canonical lineage / provenance).
const axiosOntology = axios.create({
  baseURL: defaultOntologyBase,
  timeout: 30000,
});

axiosOntology.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const token = await auth.getToken();

      setBearerAuthorization(config.headers, token);
      // Do not send X-Tenant-ID: strict tenant identity is derived from validated JWT claims.
    } catch (e) {
      console.warn('Auth not available for ontologyApi');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosOntology.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !original._retry) {
      try {
        await getAuth().refresh();
        const token = await getAuth().getToken();

        // Create a new config object with updated headers to avoid mutating readonly properties
        const retryConfig: AxiosRequestConfig & { _retry: boolean } = {
          ...original,
          _retry: true,
          headers: {
            ...original.headers,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };

        return axiosOntology(retryConfig);
      } catch (e) {
        // Prompt re-authentication instead of a silent logout mid-session.
        notifySessionExpired();
      }
    }
    return Promise.reject(error);
  }
);

export const ontologyApi = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const res: AxiosResponse<T> = await axiosOntology.get(url, config);
    return res.data;
  },
};

// ---- Canonical-lineage provenance types (mirror CanonicalLineageDtos on ontology-service) ----

export type LineageDatasetIdentity = {
  lineageDatasetId?: string | null;
  datasetType?: string | null;
  dataObjectId?: string | null;
  dataObjectVersion?: number | null;
  namespace?: string | null;
  name?: string | null;
  schemaHash?: string | null;
  ontologyEntityId?: string | null;
  evidenceLabels?: string[] | null;
};

export type LineageRunIdentity = {
  runId?: string | null;
  status?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  correlationId?: string | null;
  auditEventId?: string | null;
};

export type LineageJobIdentity = {
  jobId?: string | null;
  jobKind?: string | null;
  pipelineId?: string | null;
  transformationJobId?: string | null;
  jobName?: string | null;
  specHash?: string | null;
  configHash?: string | null;
};

export type ProducerRun = {
  run?: LineageRunIdentity | null;
  job?: LineageJobIdentity | null;
  outputDataset?: LineageDatasetIdentity | null;
  inputDatasets?: LineageDatasetIdentity[] | null;
  evidenceLabels?: string[] | null;
};

export type DownstreamConsumer = {
  run?: LineageRunIdentity | null;
  job?: LineageJobIdentity | null;
  inputDataset?: LineageDatasetIdentity | null;
  outputDatasets?: LineageDatasetIdentity[] | null;
  evidenceLabels?: string[] | null;
};

export type DataObjectProvenanceResponse = {
  queriedDataObjectId: string;
  queriedDataObjectVersion: number;
  dataset?: LineageDatasetIdentity | null;
  outputDataset?: LineageDatasetIdentity | null;
  producerRuns?: ProducerRun[] | null;
  downstreamConsumers?: DownstreamConsumer[] | null;
  evidenceLabels?: string[] | null;
  hasProvenance: boolean;
};

/**
 * Fetch canonical-lineage provenance for a specific data-object version.
 * The backend requires a concrete `version` (it 400s otherwise); callers must pass one.
 * Tenant identity is enforced server-side from the JWT — never pass a tenant hint.
 */
export const getDataObjectProvenance = (
  dataObjectId: string,
  version: number
): Promise<DataObjectProvenanceResponse> =>
  ontologyApi.get<DataObjectProvenanceResponse>(
    `/api/v1/canonical-lineage/data-objects/${encodeURIComponent(
      dataObjectId
    )}/provenance?version=${encodeURIComponent(version)}`
  );
